import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import {
    Clock,
    Mail,
    MapPin,
    Phone
} from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { ButtonLink } from '@/components/generals/button'
import { footerNavigation } from '@/constants/navigation'
import { siteConfig } from '@/constants/site'

export const Footer: FC = (): ReactNode => {
    const { phone, email, address, hours } = siteConfig.contact
    const hasContact = Boolean(phone || email || address || hours)

    return (
        <footer className='bg-ink text-ink-foreground'>
            <div className='mx-auto max-w-7xl px-5 pb-10 pt-16 sm:px-8 lg:pt-20'>
                <div className='grid gap-12 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]'>
                    <div className='max-w-sm'>
                        <Link href='/' aria-label={`${siteConfig.name} home`} className='inline-block rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand'>
                            <Logo tone='light' />
                        </Link>

                        <p className='mt-5 text-sm leading-relaxed text-ink-muted'>
                            Energy-efficient UPVC windows, doors and conservatories, made to measure and fitted with care.
                        </p>

                        <ButtonLink href='/contact' variant='brand' className='mt-7'>
                            Book a free survey
                        </ButtonLink>
                    </div>

                    {footerNavigation.map(group => (
                        <nav key={group.title} aria-label={group.title}>
                            <h2 className='font-heading text-sm font-semibold text-ink-foreground'>
                                {group.title}
                            </h2>

                            <ul className='mt-4 space-y-3'>
                                {group.links.map(link => (
                                    <li key={link.href}>
                                        <Link href={link.href} className='rounded text-sm text-ink-muted transition-colors hover:text-ink-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand'>
                                            {link.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    ))}

                    <div>
                        <h2 className='font-heading text-sm font-semibold text-ink-foreground'>
                            Get in touch
                        </h2>

                        {hasContact ? (
                            <ul className='mt-4 space-y-3 text-sm text-ink-muted'>
                                {phone && (
                                    <li className='flex items-start gap-3'>
                                        <Phone aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-brand' />
                                        <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} className='hover:text-ink-foreground'>
                                            {phone}
                                        </a>
                                    </li>
                                )}

                                {email && (
                                    <li className='flex items-start gap-3'>
                                        <Mail aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-brand' />
                                        <a href={`mailto:${email}`} className='break-all hover:text-ink-foreground'>
                                            {email}
                                        </a>
                                    </li>
                                )}

                                {address && (
                                    <li className='flex items-start gap-3'>
                                        <MapPin aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-brand' />
                                        <span>
                                            {address}
                                        </span>
                                    </li>
                                )}

                                {hours && (
                                    <li className='flex items-start gap-3'>
                                        <Clock aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-brand' />
                                        <span>
                                            {hours}
                                        </span>
                                    </li>
                                )}
                            </ul>
                        ) : (
                            <p className='mt-4 text-sm leading-relaxed text-ink-muted'>
                                Tell us about your project and we will arrange a free, no-obligation home survey.{' '}
                                <Link href='/contact' className='font-medium text-ink-foreground underline underline-offset-4 hover:text-brand'>
                                    Request a quote
                                </Link>
                            </p>
                        )}
                    </div>
                </div>

                <div className='mt-14 flex flex-col gap-3 border-t border-white/10 pt-8 text-xs text-ink-muted sm:flex-row sm:items-center sm:justify-between'>
                    <p suppressHydrationWarning>
                        &copy; {new Date().getFullYear()} {siteConfig.name}. All rights reserved.
                    </p>

                    <p>
                        {siteConfig.tagline}
                    </p>
                </div>
            </div>
        </footer>
    )
}
